import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
  ShoppingBag,
  MapPin,
  DollarSign,
  Clock,
  Shield,
  Send,
  Eye,
  CheckCircle,
  Loader2,
  ArrowLeft,
  Target,
  FileText,
  FileSearch,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  X,
  Calendar,
  Users,
  Building2,
  Home,
  Wallet,
  Gavel,
  RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Title Sidebar
function TitleSidebar({ company, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  
  const navSections = [
    {
      title: "Operations",
      items: [
        { icon: Target, label: "Command Center", path: "/title/dashboard" },
        { icon: ShoppingBag, label: "Deal Opportunities", path: "/title/opportunities", active: true },
        { icon: FileText, label: "Active Orders", path: "/title/orders" },
        { icon: ShoppingBag, label: "Marketplace", path: "/title/marketplace" },
      ]
    },
    {
      title: "Execution",
      items: [
        { icon: Wallet, label: "Deal Wallet", path: "/title/deal-wallet" },
        { icon: FileSearch, label: "Title Searches", path: "/title/searches" },
        { icon: Calendar, label: "Closings", path: "/title/closings" },
        { icon: Users, label: "Service Requests", path: "/title/requests" },
      ]
    },
    {
      title: "Intelligence",
      items: [
        { icon: DollarSign, label: "Revenue Stream", path: "/title/revenue" },
        { icon: TrendingUp, label: "Performance", path: "/title/analytics" },
        { icon: Settings, label: "Configuration", path: "/title/settings" },
      ]
    }
  ];

  return (
    <aside className={`fixed left-0 top-0 h-full bg-[#0A0A0A]/80 backdrop-blur-sm border-r border-white/[0.06] transition-all z-40 ${
      collapsed ? "w-16" : "w-64"
    }`}>
      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
        {!collapsed && (
          <Link to="/title/dashboard" className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-400" />
            <span className="text-white font-bold">TITLE PORTAL</span>
          </Link>
        )}
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 hover:bg-white/5 rounded text-white/50"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>
      
      <div className="p-4 overflow-y-auto h-[calc(100%-140px)]">
        {navSections.map((section, sIdx) => (
          <div key={sIdx} className="mb-6">
            {!collapsed && (
              <p className="text-[10px] font-semibold text-white/30 uppercase tracking-wider mb-2">
                {section.title}
              </p>
            )}
            <div className="space-y-1">
              {section.items.map((item, iIdx) => (
                <Link
                  key={iIdx}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    item.active 
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" 
                      : "text-white/50 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {!collapsed && <span className="text-sm">{item.label}</span>}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/[0.06]">
        <button
          onClick={onLogout}
          className="flex items-center gap-3 px-3 py-2 text-white/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors w-full"
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="text-sm">Logout</span>}
        </button>
      </div>
    </aside>
  );
}

// Deal Opportunity Card
function DealOpportunityCard({ deal, onBid }) {
  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5 hover:border-emerald-500/30 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Home className="w-4 h-4 text-emerald-400" />
            <h3 className="text-white font-medium">{deal.property_address}</h3>
          </div>
          <p className="text-white/40 text-xs">
            {deal.city}, {deal.state} • {deal.deal_type || 'Flip'}
          </p>
        </div>
        {deal.has_title_company ? (
          <span className="px-2 py-1 bg-white/5 border border-white/10 rounded text-[10px] text-white/50 font-semibold">
            ASSIGNED
          </span>
        ) : deal.already_bid ? (
          <span className="px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded text-[10px] text-emerald-400 font-semibold">
            BID SUBMITTED
          </span>
        ) : (
          <span className="px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded text-[10px] text-amber-400 font-semibold">
            OPEN
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-[10px] text-white/40 uppercase mb-1">Purchase Price</p>
          <p className="text-white font-mono">{formatCurrency(deal.purchase_price)}</p>
        </div>
        <div>
          <p className="text-[10px] text-white/40 uppercase mb-1">ARV</p>
          <p className="text-emerald-400 font-mono">{formatCurrency(deal.arv)}</p>
        </div>
        <div>
          <p className="text-[10px] text-white/40 uppercase mb-1">Stage</p>
          <p className="text-white capitalize">{deal.pipeline_stage?.replace(/_/g, ' ') || 'New'}</p>
        </div>
      </div>

      {deal.expected_closing_date && (
        <div className="flex items-center gap-2 mb-4 p-2 bg-black/30 rounded">
          <Calendar className="w-4 h-4 text-white/40" />
          <span className="text-white/60 text-sm">
            Expected Close: {new Date(deal.expected_closing_date).toLocaleDateString()}
          </span>
        </div>
      )}

      <div className="flex items-center gap-3">
        {deal.has_title_company ? (
          <p className="text-white/40 text-sm">Title company already assigned</p>
        ) : deal.already_bid ? (
          <div className="flex items-center gap-2 text-emerald-400">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Your bid is under review</span>
          </div>
        ) : (
          <Button
            onClick={() => onBid(deal)}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
          >
            <Gavel className="w-4 h-4 mr-2" />
            Submit Bid
          </Button>
        )}
      </div>
    </div>
  );
}

// Title Request Card (existing flow)
function TitleRequestCard({ opportunity, onBid, alreadyBid }) {
  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5 hover:border-emerald-500/30 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="w-4 h-4 text-emerald-400" />
            <h3 className="text-white font-medium">{opportunity.property_address}</h3>
          </div>
          <p className="text-white/40 text-xs">
            Request ID: {opportunity.request_id}
          </p>
        </div>
        <div className="px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded">
          <span className="text-[10px] text-emerald-400 font-semibold uppercase">Open Request</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-[10px] text-white/40 uppercase mb-1">Purchase Price</p>
          <p className="text-white font-mono">{formatCurrency(opportunity.purchase_price)}</p>
        </div>
        <div>
          <p className="text-[10px] text-white/40 uppercase mb-1">Order Type</p>
          <p className="text-emerald-400 capitalize">{opportunity.order_type || 'full'}</p>
        </div>
        <div>
          <p className="text-[10px] text-white/40 uppercase mb-1">Timeline</p>
          <p className="text-white">{opportunity.timeline_days || 30} days</p>
        </div>
      </div>

      {opportunity.notes && (
        <div className="mb-4 p-3 bg-black/50 border border-white/[0.06] rounded">
          <p className="text-[10px] text-white/40 uppercase mb-1">Operator Notes</p>
          <p className="text-white/70 text-sm">{opportunity.notes}</p>
        </div>
      )}

      <div className="flex items-center gap-3">
        {alreadyBid ? (
          <div className="flex items-center gap-2 text-emerald-400">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Interest Submitted</span>
          </div>
        ) : (
          <Button
            onClick={() => onBid(opportunity)}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
          >
            <Send className="w-4 h-4 mr-2" />
            Express Interest
          </Button>
        )}
      </div>
    </div>
  );
}

// Deal Bid Modal
function DealBidModal({ deal, onClose, onSubmit, submitting }) {
  const [form, setForm] = useState({
    title_fee: "",
    escrow_fee: "",
    closing_fee: "",
    timeline_days: 30,
    services_included: ["title_search", "title_insurance", "closing_services", "closing"],
    notes: ""
  });

  const totalFee = (parseFloat(form.title_fee) || 0) + 
                   (parseFloat(form.escrow_fee) || 0) + 
                   (parseFloat(form.closing_fee) || 0);

  const handleSubmit = () => {
    if (!form.title_fee && !form.escrow_fee && !form.closing_fee) {
      toast.error("Please enter at least one fee");
      return;
    }
    onSubmit({
      ...form,
      total_fee: totalFee
    });
  };

  const services = [
    { id: "title_search", label: "Title Search" },
    { id: "title_insurance", label: "Title Insurance" },
    { id: "closing_services", label: "Closing Services" },
    { id: "closing", label: "Closing Coordination" },
    { id: "notary", label: "Notary Services" },
    { id: "recording", label: "Recording Services" }
  ];

  const toggleService = (serviceId) => {
    setForm(prev => ({
      ...prev,
      services_included: prev.services_included.includes(serviceId)
        ? prev.services_included.filter(s => s !== serviceId)
        : [...prev.services_included, serviceId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Gavel className="w-5 h-5 text-emerald-400" />
            Submit Title Bid
          </h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Deal Info */}
        <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Home className="w-4 h-4 text-emerald-400" />
            <p className="text-emerald-400 font-medium">{deal?.property_address}</p>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <p className="text-white/40 text-xs">Purchase Price</p>
              <p className="text-white font-mono">{formatCurrency(deal?.purchase_price)}</p>
            </div>
            <div>
              <p className="text-white/40 text-xs">ARV</p>
              <p className="text-emerald-400 font-mono">{formatCurrency(deal?.arv)}</p>
            </div>
          </div>
        </div>

        {/* Fee Breakdown */}
        <div className="space-y-4 mb-6">
          <h3 className="text-white font-medium text-sm">Fee Breakdown</h3>
          
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-white/50 text-xs mb-1 block">Title Fee ($)</label>
              <Input
                type="number"
                value={form.title_fee}
                onChange={(e) => setForm({...form, title_fee: e.target.value})}
                placeholder="1,500"
                className="bg-black border-white/[0.08] text-white"
              />
            </div>
            <div>
              <label className="text-white/50 text-xs mb-1 block">Settlement Fee ($)</label>
              <Input
                type="number"
                value={form.escrow_fee}
                onChange={(e) => setForm({...form, escrow_fee: e.target.value})}
                placeholder="500"
                className="bg-black border-white/[0.08] text-white"
              />
            </div>
            <div>
              <label className="text-white/50 text-xs mb-1 block">Closing Fee ($)</label>
              <Input
                type="number"
                value={form.closing_fee}
                onChange={(e) => setForm({...form, closing_fee: e.target.value})}
                placeholder="750"
                className="bg-black border-white/[0.08] text-white"
              />
            </div>
          </div>

          <div className="p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-[#D4AF37] font-medium">Total Fee</span>
              <span className="text-[#D4AF37] font-bold text-lg">{formatCurrency(totalFee)}</span>
            </div>
          </div>
        </div>

        {/* Services Included */}
        <div className="mb-6">
          <h3 className="text-white font-medium text-sm mb-3">Services Included</h3>
          <div className="grid grid-cols-2 gap-2">
            {services.map(service => (
              <button
                key={service.id}
                onClick={() => toggleService(service.id)}
                className={`p-2 rounded border text-left text-sm transition-all ${
                  form.services_included.includes(service.id)
                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                    : "bg-black/30 border-white/[0.08] text-white/50 hover:text-white"
                }`}
              >
                <div className="flex items-center gap-2">
                  {form.services_included.includes(service.id) ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <div className="w-4 h-4 border border-white/30 rounded" />
                  )}
                  {service.label}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-6">
          <label className="text-white/70 text-sm mb-1.5 block">Estimated Timeline (days)</label>
          <Input
            type="number"
            value={form.timeline_days}
            onChange={(e) => setForm({...form, timeline_days: parseInt(e.target.value) || 30})}
            className="bg-black border-white/[0.08] text-white"
          />
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="text-white/70 text-sm mb-1.5 block">Additional Notes</label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({...form, notes: e.target.value})}
            placeholder="Any additional information about your services..."
            className="bg-black border-white/[0.08] text-white min-h-[80px]"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-white/[0.08] text-white/70"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || totalFee === 0}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Submit Bid
          </Button>
        </div>
      </div>
    </div>
  );
}

// Title Request Bid Modal (existing)
function TitleRequestBidModal({ opportunity, onClose, onSubmit, submitting }) {
  const [form, setForm] = useState({
    estimated_fee: "",
    timeline_days: opportunity?.timeline_days || 30,
    notes: ""
  });

  const handleSubmit = () => {
    if (!form.estimated_fee) {
      toast.error("Please enter an estimated fee");
      return;
    }
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-lg max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Express Interest</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded">
          <p className="text-emerald-400 font-medium text-sm">{opportunity?.property_address}</p>
          <p className="text-white/50 text-xs mt-1">
            Purchase: {formatCurrency(opportunity?.purchase_price)} • {opportunity?.order_type || 'Full'} title order
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Estimated Total Fee ($)</label>
            <Input
              type="number"
              value={form.estimated_fee}
              onChange={(e) => setForm({...form, estimated_fee: e.target.value})}
              placeholder="2,500"
              className="bg-black border-white/[0.08] text-white"
            />
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Timeline (days)</label>
            <Input
              type="number"
              value={form.timeline_days}
              onChange={(e) => setForm({...form, timeline_days: parseInt(e.target.value) || 30})}
              className="bg-black border-white/[0.08] text-white"
            />
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Notes (optional)</label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              placeholder="Additional details about your services..."
              className="bg-black border-white/[0.08] text-white min-h-[80px]"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-white/[0.08] text-white/70"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Submit Interest
          </Button>
        </div>
      </div>
    </div>
  );
}

// Main Page Component
export default function TitleOpportunitiesPage() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("deals"); // deals or requests
  
  // Deal opportunities
  const [dealOpportunities, setDealOpportunities] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  
  // Title requests (existing)
  const [titleRequests, setTitleRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/title/me`, { withCredentials: true });
      setCompany(response.data);
      fetchDealOpportunities();
      fetchTitleRequests();
    } catch (error) {
      navigate("/title/login");
    } finally {
      setLoading(false);
    }
  };

  const fetchDealOpportunities = async () => {
    setLoadingDeals(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/title/deal-opportunities`, {
        withCredentials: true
      });
      setDealOpportunities(response.data.deals || []);
    } catch (error) {
      console.error("Failed to fetch deal opportunities:", error);
    } finally {
      setLoadingDeals(false);
    }
  };

  const fetchTitleRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/title/title-opportunities`, {
        withCredentials: true
      });
      setTitleRequests(response.data || []);
    } catch (error) {
      console.error("Failed to fetch title requests:", error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleDealBidSubmit = async (form) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/title/bid-on-deal`,
        {
          deal_id: selectedDeal.deal_id,
          ...form
        },
        { withCredentials: true }
      );
      toast.success("Bid submitted successfully!");
      setSelectedDeal(null);
      fetchDealOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit bid");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestBidSubmit = async (form) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/title/express-interest`,
        {
          title_request_id: selectedRequest.request_id,
          ...form
        },
        { withCredentials: true }
      );
      toast.success("Interest submitted!");
      setSelectedRequest(null);
      fetchTitleRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit interest");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/title/logout`, {}, { withCredentials: true });
      navigate("/title/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  const openDeals = dealOpportunities.filter(d => !d.has_title_company);
  const openRequests = titleRequests.filter(r => r.status === "open");

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="title-opportunities-page">
      <TitleSidebar company={company} onLogout={handleLogout} />
      
      <main className="ml-64 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <ShoppingBag className="w-8 h-8 text-emerald-400" />
              Deal Opportunities
            </h1>
            <p className="text-white/50 mt-1">
              Browse deals and submit bids to become the escrow authority
            </p>
          </div>
          <Button
            onClick={() => { fetchDealOpportunities(); fetchTitleRequests(); }}
            className="bg-white/5 hover:bg-white/10 text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab("deals")}
            className={`${activeTab === "deals" 
              ? "bg-emerald-500 text-white" 
              : "bg-white/5 text-white/70 hover:bg-white/10"}`}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Deal Opportunities ({openDeals.length})
          </Button>
          <Button
            onClick={() => setActiveTab("requests")}
            className={`${activeTab === "requests" 
              ? "bg-emerald-500 text-white" 
              : "bg-white/5 text-white/70 hover:bg-white/10"}`}
          >
            <FileText className="w-4 h-4 mr-2" />
            Title Requests ({openRequests.length})
          </Button>
        </div>

        {/* Content */}
        {activeTab === "deals" ? (
          <>
            {loadingDeals ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
              </div>
            ) : openDeals.length === 0 ? (
              <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-12 text-center">
                <Building2 className="w-16 h-16 text-white/10 mx-auto mb-4" />
                <p className="text-white/50">No open deal opportunities at this time</p>
                <p className="text-white/30 text-sm mt-1">
                  Check back later for new deals seeking title services
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {openDeals.map(deal => (
                  <DealOpportunityCard
                    key={deal.deal_id}
                    deal={deal}
                    onBid={setSelectedDeal}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {loadingRequests ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
              </div>
            ) : openRequests.length === 0 ? (
              <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-12 text-center">
                <FileText className="w-16 h-16 text-white/10 mx-auto mb-4" />
                <p className="text-white/50">No open title requests at this time</p>
                <p className="text-white/30 text-sm mt-1">
                  Check back later for new title service requests
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {openRequests.map((request, idx) => (
                  <TitleRequestCard
                    key={request.request_id || idx}
                    opportunity={request}
                    onBid={setSelectedRequest}
                    alreadyBid={company && (request.interested_companies || []).includes(company.company_id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Deal Bid Modal */}
      {selectedDeal && (
        <DealBidModal
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onSubmit={handleDealBidSubmit}
          submitting={submitting}
        />
      )}

      {/* Title Request Bid Modal */}
      {selectedRequest && (
        <TitleRequestBidModal
          opportunity={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onSubmit={handleRequestBidSubmit}
          submitting={submitting}
        />
      )}
    </div>
  );
}
